import {
  accountMutationHeaders,
  readBoundedAccountJson,
} from "./account-state-session-repository.js";
import {
  createAccountOperationId,
  isAccountOperationId,
} from "./account-operation-id.js";

const SCHEMA_VERSION = 1;
const RESPONSE_MAX_BYTES = 8 * 1024;
const ACCOUNT_PATH = "/api/v1/account/notice-disclosure-preferences";
const VALUE_FIELDS = Object.freeze([
  "guideTranslationHelp",
  "calculatorDataQuality",
  "mapZonePane",
]);
const COMMAND_FIELDS = Object.freeze({
  create: ["kind", "schemaVersion", "operationId", "values"],
  set: [
    "kind",
    "schemaVersion",
    "operationId",
    "expectedRevision",
    "values",
  ],
  reset: ["kind", "schemaVersion", "operationId", "expectedRevision"],
});
const DEFAULT_VALUES = Object.freeze({
  calculatorDataQuality: null,
  guideTranslationHelp: null,
  mapZonePane: null,
});

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactFields(value, expectedFields) {
  if (!isRecord(value)) return false;
  const actualFields = Object.keys(value).sort();
  const sortedExpected = expectedFields.slice().sort();
  return actualFields.length === sortedExpected.length
    && actualFields.every((field, index) => field === sortedExpected[index]);
}

function positiveSafeInteger(value) {
  return Number.isSafeInteger(value) && value > 0;
}

export function decodeNoticeDisclosurePreferenceValuesV1(value) {
  if (
    !hasExactFields(value, VALUE_FIELDS)
    || VALUE_FIELDS.some((field) => (
      value[field] !== null && typeof value[field] !== "boolean"
    ))
  ) {
    throw new TypeError("Account notice disclosure preference values are invalid.");
  }
  return Object.freeze({
    calculatorDataQuality: value.calculatorDataQuality,
    guideTranslationHelp: value.guideTranslationHelp,
    mapZonePane: value.mapZonePane,
  });
}

function freezeProjection(value) {
  return value.kind === "missing"
    ? Object.freeze({ kind: "missing", schemaVersion: SCHEMA_VERSION })
    : Object.freeze({
      kind: "ready",
      schemaVersion: SCHEMA_VERSION,
      revision: value.revision,
      values: decodeNoticeDisclosurePreferenceValuesV1(value.values),
    });
}

function freezeState(value) {
  const next = { ...value };
  if (next.authoritativeState) {
    next.authoritativeState = freezeProjection(next.authoritativeState);
  }
  return Object.freeze(next);
}

export function decodeNoticeDisclosurePreferencesProjectionV1(value) {
  if (
    hasExactFields(value, ["kind", "schemaVersion"])
    && value.kind === "missing"
    && value.schemaVersion === SCHEMA_VERSION
  ) {
    return freezeProjection(value);
  }
  if (
    !hasExactFields(value, ["kind", "schemaVersion", "revision", "values"])
    || value.kind !== "ready"
    || value.schemaVersion !== SCHEMA_VERSION
    || !positiveSafeInteger(value.revision)
  ) {
    throw new TypeError("Account notice disclosure preferences projection is invalid.");
  }
  return freezeProjection(value);
}

export function decodeNoticeDisclosurePreferencesCommandV1(value) {
  const expectedFields = COMMAND_FIELDS[value?.kind];
  if (
    !expectedFields
    || !hasExactFields(value, expectedFields)
    || value.schemaVersion !== SCHEMA_VERSION
    || !isAccountOperationId(value.operationId)
    || (
      ["set", "reset"].includes(value.kind)
      && !positiveSafeInteger(value.expectedRevision)
    )
  ) {
    throw new TypeError("Account notice disclosure preferences command is invalid.");
  }
  return Object.freeze({
    ...value,
    ...(["create", "set"].includes(value.kind)
      ? { values: decodeNoticeDisclosurePreferenceValuesV1(value.values) }
      : {}),
  });
}

export function decodeNoticeDisclosurePreferencesAcknowledgementV1(
  value,
  operationId,
) {
  if (
    !hasExactFields(value, ["kind", "operationId", "authoritativeState"])
    || !["applied", "conflict"].includes(value.kind)
    || value.operationId !== operationId
  ) {
    throw new TypeError(
      "Account notice disclosure preferences acknowledgement is invalid.",
    );
  }
  return Object.freeze({
    kind: value.kind,
    operationId,
    authoritativeState:
      decodeNoticeDisclosurePreferencesProjectionV1(value.authoritativeState),
  });
}

function accountUrl(apiBaseUrl) {
  let base;
  try {
    base = new URL(String(apiBaseUrl ?? ""));
  } catch (_error) {
    throw new TypeError("Notice disclosure preferences API base URL is invalid.");
  }
  if (
    !["http:", "https:"].includes(base.protocol)
    || base.username
    || base.password
    || base.pathname !== "/"
    || base.search
    || base.hash
  ) {
    throw new TypeError(
      "Notice disclosure preferences API base URL must be an HTTP origin.",
    );
  }
  return `${base.origin}${ACCOUNT_PATH}`;
}

export function createNoticeDisclosurePreferencesAccountProvider({
  apiBaseUrl,
  fetchImpl = globalThis.fetch,
} = {}) {
  if (typeof fetchImpl !== "function") {
    throw new TypeError("Notice disclosure preferences provider requires fetch.");
  }
  const url = accountUrl(apiBaseUrl);

  async function load({ signal } = {}) {
    const response = await fetchImpl(url, {
      method: "GET",
      cache: "no-store",
      credentials: "include",
      headers: { Accept: "application/json" },
      redirect: "error",
      signal,
    });
    if (response.status === 401) return freezeState({ kind: "signedOut" });
    if (!response.ok) {
      throw new Error("Notice disclosure preferences request failed.");
    }
    return decodeNoticeDisclosurePreferencesProjectionV1(
      await readBoundedAccountJson(
        response,
        "Account notice disclosure preferences",
        RESPONSE_MAX_BYTES,
      ),
    );
  }

  async function mutate(session, command, { signal } = {}) {
    const exactCommand = decodeNoticeDisclosurePreferencesCommandV1(command);
    const response = await fetchImpl(url, {
      method: "POST",
      cache: "no-store",
      credentials: "include",
      headers: accountMutationHeaders(session),
      redirect: "error",
      signal,
      body: JSON.stringify(exactCommand),
    });
    if (response.status === 401) return freezeState({ kind: "signedOut" });
    if (![200, 409].includes(response.status)) {
      throw new Error("Notice disclosure preferences mutation failed.");
    }
    const acknowledgement = decodeNoticeDisclosurePreferencesAcknowledgementV1(
      await readBoundedAccountJson(
        response,
        "Account notice disclosure preferences acknowledgement",
        RESPONSE_MAX_BYTES,
      ),
      exactCommand.operationId,
    );
    if (
      (response.status === 200 && acknowledgement.kind !== "applied")
      || (response.status === 409 && acknowledgement.kind !== "conflict")
    ) {
      throw new TypeError(
        "Notice disclosure preferences status and acknowledgement disagree.",
      );
    }
    return acknowledgement;
  }

  return Object.freeze({ load, mutate });
}

function hasMethods(value, names) {
  return names.every((name) => typeof value?.[name] === "function");
}

function expectedRevision(options, label) {
  if (options === undefined) return undefined;
  if (
    !isRecord(options)
    || Object.keys(options).some((field) => field !== "expectedRevision")
  ) {
    throw new TypeError(`${label} options contain unsupported fields.`);
  }
  if (options.expectedRevision === undefined || options.expectedRevision === null) {
    return options.expectedRevision;
  }
  if (!positiveSafeInteger(options.expectedRevision)) {
    throw new TypeError(`${label} expected revision is invalid.`);
  }
  return options.expectedRevision;
}

function valuesEqual(left, right) {
  return VALUE_FIELDS.every((field) => left[field] === right[field]);
}

export function createAuthenticatedNoticeDisclosurePreferencesRepository({
  sessionRepository,
  accountProvider = null,
  apiBaseUrl,
  fetchImpl = globalThis.fetch,
  createOperationId = createAccountOperationId,
} = {}) {
  if (!hasMethods(sessionRepository, ["load", "refresh", "state", "subscribe"])) {
    throw new TypeError(
      "Notice disclosure preferences require the account session repository.",
    );
  }
  const remote = accountProvider
    ?? createNoticeDisclosurePreferencesAccountProvider({ apiBaseUrl, fetchImpl });
  if (
    !hasMethods(remote, ["load", "mutate"])
    || typeof createOperationId !== "function"
  ) {
    throw new TypeError(
      "Notice disclosure preferences require their typed account provider.",
    );
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let authoritativeState = null;
  let accountId = null;
  let requestId = 0;
  let requestController = null;
  let sessionLoadDepth = 0;
  let disposed = false;
  const listeners = new Set();

  function state() {
    return currentState;
  }

  function deliver(listener) {
    try {
      listener(currentState);
    } catch (_error) {
      globalThis.reportError?.(
        new Error("Notice disclosure preferences subscriber failed."),
      );
    }
  }

  function publish(value) {
    if (disposed) return currentState;
    const nextState = freezeState(value);
    if (JSON.stringify(currentState) === JSON.stringify(nextState)) {
      return currentState;
    }
    currentState = nextState;
    for (const listener of listeners) deliver(listener);
    return currentState;
  }

  function beginRequest({ loading = false } = {}) {
    requestId += 1;
    requestController?.abort();
    requestController = new AbortController();
    if (loading) publish({ kind: "loading" });
    return Object.freeze({ id: requestId, controller: requestController });
  }

  function isCurrent(request) {
    return !disposed
      && request.id === requestId
      && !request.controller.signal.aborted;
  }

  function clearAuthority(value) {
    authoritativeState = null;
    accountId = null;
    return publish(value);
  }

  function applyProjection(projection, nextAccountId) {
    authoritativeState = projection;
    accountId = nextAccountId;
    return publish({ kind: "ready", authoritativeState: projection });
  }

  function unavailable(operation, reason) {
    return clearAuthority({ kind: "unavailable", operation, reason });
  }

  async function freshSession(request, { refresh = false } = {}) {
    sessionLoadDepth += 1;
    try {
      const session = await sessionRepository[refresh ? "refresh" : "load"]();
      return isCurrent(request) ? session : null;
    } finally {
      sessionLoadDepth -= 1;
    }
  }

  function applySessionBoundary(session) {
    if (session?.kind === "signedOut") {
      return clearAuthority({ kind: "signedOut" });
    }
    if (session?.kind === "locked") {
      return clearAuthority({
        kind: "locked",
        reason: session.reason || "account-locked",
      });
    }
    if (session?.kind === "unavailable") {
      return unavailable("account", session.reason || "session-request-failed");
    }
    if (session?.kind !== "ready") {
      return clearAuthority({ kind: "loading" });
    }
    return null;
  }

  async function readRemote(request, session) {
    try {
      const projection = await remote.load({ signal: request.controller.signal });
      if (!isCurrent(request)) return state();
      if (projection.kind === "signedOut") {
        const refreshed = await freshSession(request, { refresh: true });
        if (!refreshed || !isCurrent(request)) return state();
        if (applySessionBoundary(refreshed) || refreshed.kind !== "ready") {
          return state();
        }
        return unavailable("load", "account-authorization-rejected");
      }
      return applyProjection(projection, session.accountId);
    } catch (error) {
      if (!isCurrent(request) || error?.name === "AbortError") return state();
      return unavailable("load", "notice-disclosure-request-failed");
    }
  }

  async function load() {
    if (disposed) return state();
    const request = beginRequest({ loading: true });
    const session = await freshSession(request);
    if (!session || !isCurrent(request)) return state();
    if (applySessionBoundary(session) || session.kind !== "ready") return state();
    return readRemote(request, session);
  }

  function operationId() {
    const value = createOperationId();
    if (!isAccountOperationId(value)) {
      throw new TypeError("Notice disclosure preferences operation ID is invalid.");
    }
    return value;
  }

  function currentValues() {
    return authoritativeState?.kind === "ready"
      ? authoritativeState.values
      : DEFAULT_VALUES;
  }

  function commandFor(nextValues, reset) {
    if (!authoritativeState) return null;
    if (reset) {
      return authoritativeState.kind === "missing"
        ? null
        : decodeNoticeDisclosurePreferencesCommandV1({
          kind: "reset",
          schemaVersion: SCHEMA_VERSION,
          operationId: operationId(),
          expectedRevision: authoritativeState.revision,
        });
    }
    return decodeNoticeDisclosurePreferencesCommandV1({
      kind: authoritativeState.kind === "missing" ? "create" : "set",
      schemaVersion: SCHEMA_VERSION,
      operationId: operationId(),
      ...(authoritativeState.kind === "ready"
        ? { expectedRevision: authoritativeState.revision }
        : {}),
      values: nextValues,
    });
  }

  function conflict(operation, expected) {
    return publish({
      kind: "conflict",
      operation,
      expectedRevision: expected,
      authoritativeState,
    });
  }

  async function mutate(operation, nextValues, options, { reset = false } = {}) {
    const expected = expectedRevision(options, `Notice disclosure ${operation}`);
    if (currentState.kind === "pending") return state();
    if (!authoritativeState || !accountId) return state();
    const currentRevision = authoritativeState.kind === "ready"
      ? authoritativeState.revision
      : null;
    if (!reset && valuesEqual(currentValues(), nextValues)) return state();
    if (expected !== undefined && expected !== currentRevision) {
      return conflict(operation, expected);
    }
    const command = commandFor(nextValues, reset);
    if (!command) return state();
    const startingAccountId = accountId;
    const request = beginRequest();
    publish({ kind: "pending", operation, authoritativeState });

    try {
      const session = await freshSession(request, { refresh: true });
      if (!session || !isCurrent(request)) return state();
      if (applySessionBoundary(session) || session.kind !== "ready") return state();
      if (session.accountId !== startingAccountId) {
        return unavailable(operation, "account-authority-changed");
      }
      const acknowledgement = await remote.mutate(session, command, {
        signal: request.controller.signal,
      });
      if (!isCurrent(request)) return state();
      if (acknowledgement.kind === "signedOut") {
        const refreshed = await freshSession(request, { refresh: true });
        if (!refreshed || !isCurrent(request)) return state();
        if (applySessionBoundary(refreshed) || refreshed.kind !== "ready") {
          return state();
        }
        return unavailable(operation, "account-authorization-rejected");
      }
      authoritativeState = acknowledgement.authoritativeState;
      accountId = session.accountId;
      if (acknowledgement.kind === "conflict") {
        return publish({
          kind: "conflict",
          operation,
          expectedRevision: currentRevision,
          authoritativeState,
        });
      }
      return publish({ kind: "ready", authoritativeState });
    } catch (error) {
      if (!isCurrent(request) || error?.name === "AbortError") return state();
      return unavailable(operation, "notice-disclosure-mutation-failed");
    }
  }

  function setValues(values, options) {
    return mutate(
      "setValues",
      decodeNoticeDisclosurePreferenceValuesV1(values),
      options,
    );
  }

  function reset(options) {
    return mutate("reset", DEFAULT_VALUES, options, { reset: true });
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError(
        "Notice disclosure preferences listener must be a function.",
      );
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  const unsubscribeSession = sessionRepository.subscribe((session) => {
    if (disposed || sessionLoadDepth > 0) return;
    if (session.kind !== "ready") {
      beginRequest();
      applySessionBoundary(session);
      return;
    }
    const request = beginRequest({ loading: true });
    void readRemote(request, session);
  });

  function dispose() {
    if (disposed) return;
    disposed = true;
    requestId += 1;
    requestController?.abort();
    requestController = null;
    listeners.clear();
    unsubscribeSession();
  }

  return Object.freeze({
    dispose,
    load,
    reset,
    setValues,
    state,
    subscribe,
  });
}
